import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import moment from "moment";
import { UserAvatar, Label } from "src/components";
import { useMentions } from "src/hooks";
import { wrap_image_with_anchor_and_open_in_new_tab } from "src/utils/regex";
import { useAppContext } from "src/hooks";
import MentionMemoEditor from "./MentionMemoEditor";
import MentionDetailPopover from "./MentionDetailPopover";

import { autoMarkAsReadCommentsAfterViewing } from "src/constants/constants";
// ==========================================================

function ReplyRow({
  comment,
  reply,
  isFirst,
  isLast,
  replyTarget,
  setReplyTarget,
  onEditClick,
  onDelComment,
  editingCommentId,
  editCommentTitle,
  handleUpdateTaskComment,
  markCommentAsRead,
  onSubmitReply,
  replyValues,
  setReplyValues,
  selectedTask,
  canManageReply,
  attachments,
  projectTeam,
}) {
  const { dispatch_get_user_profile } = useAppContext();
  const userId = dispatch_get_user_profile().user_id;
  const editorRef = useRef();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const {
    displayMentionName,
    handleMentionHover,
    handleMentionLeave,
    mentionAnchorEl,
    hoveredUser,
  } = useMentions(
    [
      // include task creator in task team for @mention hover
      ...(selectedTask?.team || []),
      ...(selectedTask?.creator_profile &&
      !(selectedTask?.team || []).some(
        (u) => u.user_id === selectedTask.creator_profile.user_id
      )
        ? [selectedTask.creator_profile]
        : []),
    ],
    attachments,
    projectTeam
  );
  const is_mobile_view = useMediaQuery("(max-width:600px)");

  const isUnread = reply?.unread_users?.some(
    (u) => u.user_id === dispatch_get_user_profile()?.user_id
  );
  const canEditReply = reply?.creator?.user_id === userId;
  const canDeleteReply =
    reply?.creator?.user_id === userId ||
    String(selectedTask?.role).toLowerCase() === "admin";

  // =================== Auto mark as read ===================
  const chipRef = useRef(null);

  useEffect(() => {
    if (!isUnread || !chipRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            autoMarkAsReadCommentsAfterViewing(reply._id, markCommentAsRead);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(chipRef.current);

    return () => observer.disconnect();
  }, [isUnread, reply._id, markCommentAsRead]);
  // ==========================================================

  return (
    <Box sx={{ mt: 1, pr: 1, position: "relative" }}>
      <Box
        className="comment-reply-line-horizental"
        sx={{
          position: "absolute",
          left: is_mobile_view ? "-28px" : "-34px",
          top: "-2px",
          width: is_mobile_view ? "28px" : "34px",
          height: "16px",
          borderLeft: "1px solid #bdbdbda1",
          borderBottom: "1px solid #bdbdbda1",
          borderBottomLeftRadius: "16px",
        }}
      />
      {!isLast && (
        <Box
          className="comment-reply-line-vertical"
          sx={{
            position: "absolute",
            left: is_mobile_view ? "-28px" : "-34px",
            top: 0,
            bottom: "-7px",
            width: 0,
            borderLeft: "1px solid #bdbdbda1",
            boxSizing: "border-box",
          }}
        />
      )}
      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
        <UserAvatar
          image={reply?.creator?.image}
          alt={reply?.creator?.first_name}
          name={reply?.creator?.first_name}
          sx={{ width: 24, height: 24 }}
        />
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontSize: "12px", fontWeight: 700 }}>
                {reply?.creator?.first_name} {reply?.creator?.last_name}
              </Typography>
              <Typography sx={{ fontSize: "11px" }}>
                {moment(reply?.created_at).format("MMM DD [at] hh:mm a")}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {reply?.edited_at && (
                <Tooltip
                  title={`Edited ${moment(reply?.edited_at).format(
                    "MMM DD [at] hh:mm a"
                  )}`}
                >
                  <span
                    style={{
                      fontSize: "11px",
                      color: "#434a50",
                      marginRight: reply?.unread_users?.some(
                        (u) =>
                          u.user_id === dispatch_get_user_profile()?.user_id
                      )
                        ? "8px"
                        : "0px",
                    }}
                  >
                    Edited
                  </span>
                </Tooltip>
              )}
              {reply?.unread_users?.some(
                (u) => u.user_id === dispatch_get_user_profile()?.user_id
              ) && (
                <Button
                  ref={chipRef}
                  size="small"
                  color="inherit"
                  variant="outlined"
                  sx={{
                    height: "20px",
                    fontSize: "10px",
                    textTransform: "none",
                    padding: "0 6px",
                    minWidth: "auto",
                    borderRadius: "4px",
                    ":hover": {
                      backgroundColor: "transparent",
                      color: "#00AB55",
                      borderColor: "#00ab553b",
                    },
                  }}
                  onClick={() => markCommentAsRead?.("single", reply._id)}
                >
                  Mark as read
                </Button>
              )}
              {(canEditReply || canDeleteReply) && (
                <>
                  <IconButton
                    size="small"
                    onClick={(e) => setMenuAnchor(e.currentTarget)}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                  <Menu
                    anchorEl={menuAnchor}
                    open={Boolean(menuAnchor)}
                    onClose={() => setMenuAnchor(null)}
                  >
                    {canEditReply && (
                      <MenuItem
                        onClick={() => {
                          setMenuAnchor(null);
                          onEditClick(reply);
                        }}
                        sx={{ fontSize: "12px" }}
                      >
                        Edit
                      </MenuItem>
                    )}
                    {canDeleteReply && (
                      <MenuItem
                        onClick={() => {
                          setMenuAnchor(null);
                          onDelComment(reply);
                        }}
                        sx={{ fontSize: "12px" }}
                      >
                        Delete
                      </MenuItem>
                    )}
                  </Menu>
                </>
              )}
            </Box>
          </Box>

          {editingCommentId !== reply._id ? (
            <Box sx={{ position: "relative" }}>
              {reply?.unread_users?.some(
                (u) => u.user_id === dispatch_get_user_profile()?.user_id
              ) && (
                <div
                  style={{
                    position: "absolute",
                    top: "2px",
                    right: "2px",
                    height: "8px",
                    width: "8px",
                    backgroundColor: "#FF4842",
                    borderRadius: "50%",
                  }}
                />
              )}
              <Box
                className={`comment-data ${
                  reply?.unread_users?.some(
                    (u) => u.user_id === dispatch_get_user_profile()?.user_id
                  )
                    ? "unread-comment"
                    : ""
                }`}
                sx={{
                  mt: 0.5,
                  fontSize: "13px",
                  lineHeight: 1.45,
                  wordBreak: "break-word",
                }}
                dangerouslySetInnerHTML={{
                  __html: displayMentionName(
                    wrap_image_with_anchor_and_open_in_new_tab(
                      reply?.comment_title || ""
                    )
                  ),
                }}
                onMouseMove={handleMentionHover}
                onMouseLeave={handleMentionLeave}
              />
              <MentionDetailPopover
                anchorEl={mentionAnchorEl}
                user={hoveredUser}
                onClose={handleMentionLeave}
                showDetail={true}
                taskCreatorId={selectedTask?.creator_profile?.user_id}
              />
            </Box>
          ) : (
            <Box sx={{ mt: 1 }}>
              <MentionMemoEditor
                ref={editorRef}
                value={editCommentTitle}
                team={[
                  // include task creator in task team for @mention
                  ...(selectedTask?.team || []),
                  ...(selectedTask?.creator_profile &&
                  !(selectedTask?.team || []).some(
                    (u) => u.user_id === selectedTask.creator_profile.user_id
                  )
                    ? [selectedTask.creator_profile]
                    : []),
                ]}
                mentionableTeam={
                  selectedTask?.role === "client" ||
                  selectedTask?.role === "client_team"
                    ? [
                        // include task creator in task team for @mention
                        ...(selectedTask?.team?.filter((user) =>
                          projectTeam?.some((pu) => pu.user_id === user.user_id)
                        ) || []),
                        ...(selectedTask?.creator_profile &&
                        !(
                          selectedTask?.team?.filter((user) =>
                            projectTeam?.some(
                              (pu) => pu.user_id === user.user_id
                            )
                          ) || []
                        ).some(
                          (u) =>
                            u.user_id === selectedTask.creator_profile.user_id
                        )
                          ? [selectedTask.creator_profile]
                          : []),
                      ]
                    : [
                        ...(selectedTask?.team || []),
                        ...(selectedTask?.creator_profile &&
                        !(selectedTask?.team || []).some(
                          (u) =>
                            u.user_id === selectedTask.creator_profile.user_id
                        )
                          ? [selectedTask.creator_profile]
                          : []),
                      ]
                }
                // mentionableTeam={
                //   selectedTask?.role === "client" ||
                //   selectedTask?.role === "client_team"
                //     ? selectedTask?.team?.filter((user) =>
                //         projectTeam?.some((pu) => pu.user_id === user.user_id)
                //       )
                //     : selectedTask?.team
                canMention={true}
                attachments={attachments || []}
              />
              <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={(e) =>
                    handleUpdateTaskComment(
                      e,
                      "detail",
                      editorRef.current?.getValue(),
                      reply
                    )
                  }
                >
                  Save
                </Button>
                <Button
                  size="small"
                  variant="text"
                  color="inherit"
                  onClick={() => onEditClick({ _id: "" })}
                >
                  cancel
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function ReplyComment({
  comment,
  replies = [],
  replyTarget,
  setReplyTarget,
  replyValues,
  setReplyValues,
  onSubmitReply,
  onEditClick,
  onDelComment,
  editingCommentId,
  editCommentTitle,
  handleUpdateTaskComment,
  markCommentAsRead,
  selectedTask,
  canManageReply,
  attachments,
  projectTeam,
}) {
  return (
    <Box sx={{ mt: 1, pl: 0.5, position: "relative" }}>
      {replies.map((reply, index) => (
        <ReplyRow
          key={reply._id}
          comment={comment}
          reply={reply}
          isFirst={index === 0}
          isLast={index === replies.length - 1}
          replyTarget={replyTarget}
          setReplyTarget={setReplyTarget}
          onEditClick={onEditClick}
          onDelComment={onDelComment}
          editingCommentId={editingCommentId}
          editCommentTitle={editCommentTitle}
          handleUpdateTaskComment={handleUpdateTaskComment}
          markCommentAsRead={markCommentAsRead}
          onSubmitReply={onSubmitReply}
          replyValues={replyValues}
          setReplyValues={setReplyValues}
          selectedTask={selectedTask}
          canManageReply={canManageReply}
          attachments={attachments || []}
          projectTeam={projectTeam}
        />
      ))}
    </Box>
  );
}

export default ReplyComment;
